import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@jana-technology/platform-ui'

// To be defined in backend API so we can export it as types via OpenAPI?
// Add filterprofile permissions used by the UI for create/read checks
const PERMISSIONS_STUB = [
  'phmsa:export',
  'healthcheck:read',
  'filterprofile:read',
  'filterprofile:create'
] as const

type Permission = (typeof PERMISSIONS_STUB)[number]

const useHasPermissions = () => {
  const { isAuthenticated, getTokenSilently } = useAuth()
  const [accessToken, setAccessToken] = useState<string | null>(null)

  useEffect(() => {
    // Prevent setting state if unmounted
    let isMounted = true

    if (isAuthenticated) {
      getTokenSilently()
        .then((token) => {
          if (isMounted) {
            setAccessToken(token)
          }
        })
        .catch((error) => {
          console.error('Auth token retrieval failed:', error)

          if (isMounted) {
            setAccessToken(null)
          }
        })
    } else {
      setAccessToken(null)
    }

    return () => {
      isMounted = false
    }
  }, [isAuthenticated, getTokenSilently])

  const getPermissions = useMemo(() => {
    return () => {
      if (!accessToken) {
        return []
      }

      try {
        const decodedToken = JSON.parse(atob(accessToken.split('.')[1]))
        const permissions = decodedToken.permissions as Permission[]

        return permissions
      } catch (error) {
        console.error('Error retrieving permissions from token:', error)

        return []
      }
    }
  }, [accessToken])

  const hasPermissions = useMemo(() => {
    return (requiredPermissions: Permission[]): boolean => {
      // retrive user permission
      const userPermissions = getPermissions()

      // Check if all required permissions are present in the user's permissions
      const userHasPermissions = requiredPermissions.every((permission) =>
        userPermissions.includes(permission)
      )

      return userHasPermissions
    }
  }, [getPermissions])

  return hasPermissions
}

export { type Permission, useHasPermissions }
